if (!customElements.get('listbox-picker')) {
  // Add to Cart stays disabled until the shopper has picked a value in every
  // listbox on the product (see buy-buttons.liquid). Shopify always has a
  // variant selected under the hood, so without this an untouched picker
  // would add the first variant's finish to the cart.
  const updatePurchaseGate = (container) => {
    if (!container) return;
    const pickers = Array.from(container.querySelectorAll('listbox-picker'));
    if (pickers.length === 0) return;

    const unpicked = pickers.find((picker) => !picker.isPicked);
    const button = container.querySelector('.product-form__submit');
    const buttonText = button?.querySelector('span');

    if (unpicked) {
      container.setAttribute('data-needs-selection', '');
      if (button) {
        button.setAttribute('disabled', 'disabled');
        if (buttonText) buttonText.textContent = unpicked.chooseText;
      }
      return;
    }

    container.removeAttribute('data-needs-selection');
    if (!button || !buttonText) return;

    // Every option is picked: restore the button from the selected variant
    // the server rendered, as product-info.js does after a variant change.
    const variantData = container.querySelector(
      'variant-selects [data-selected-variant]',
    )?.textContent;
    let variant = null;
    try {
      variant = variantData ? JSON.parse(variantData) : null;
    } catch (error) {
      variant = null;
    }

    if (variant && variant.available) {
      button.removeAttribute('disabled');
      buttonText.textContent = window.variantStrings.addToCart;
    } else {
      button.setAttribute('disabled', 'disabled');
      buttonText.textContent = variant
        ? window.variantStrings.soldOut
        : window.variantStrings.unavailable;
    }
  };

  // product-info.js re-enables the button from the server HTML after every
  // variant change; re-apply the gate after it.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
      document
        .querySelectorAll(`[data-section="${data.sectionId}"][data-product-id]`)
        .forEach(updatePurchaseGate);
    });
  }

  customElements.define(
    'listbox-picker',
    class ListboxPicker extends HTMLElement {
      constructor() {
        super();
        this.trigger = this.querySelector('.listbox-picker__trigger');
        this.panel = this.querySelector('.listbox-picker__panel');
        this.swatch = this.querySelector('[data-listbox-swatch]');
        this.label = this.querySelector('[data-listbox-label]');
        this.isOpen = false;
        this.handleOutsideClick = this.handleOutsideClick.bind(this);
      }

      connectedCallback() {
        this.trigger.addEventListener('click', () => this.toggle());
        this.addEventListener('change', this.handleChange.bind(this));
        this.panel.addEventListener('click', this.handleRowClick.bind(this));
        this.addEventListener('keydown', this.handleKeydown.bind(this));
        document.addEventListener('click', this.handleOutsideClick);

        // product-info.js refetches and fully replaces <variant-selects>
        // (and everything inside it) on every option change, and the fresh
        // HTML marks every option's radio checked from the resolved variant.
        // Only show a value for options the shopper actually picked this
        // session, so the others stay on "Select from…".
        if (this.isPicked) this.applySelection(this.checkedInput);

        // All pickers reconnect together after a swap; gate once they have.
        queueMicrotask(() => updatePurchaseGate(this.container));
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.handleOutsideClick);
      }

      get container() {
        return this.closest('[data-product-id]');
      }

      get pickedKey() {
        const productId = this.container?.dataset.productId || 'unknown';
        return `listbox-picker-picked:${productId}:${this.dataset.optionName}`;
      }

      get checkedInput() {
        return this.querySelector('.listbox-picker__input:checked');
      }

      // Picked = chosen this session and still checked in the current HTML
      // (a reload without ?variant= renders nothing checked).
      get isPicked() {
        return (
          sessionStorage.getItem(this.pickedKey) === '1' &&
          Boolean(this.checkedInput)
        );
      }

      get chooseText() {
        const name = (this.dataset.optionName || '').toLowerCase();
        const article = /^[aeiou]/.test(name) ? 'an' : 'a';
        return `Choose ${article} ${name}`;
      }

      toggle() {
        if (this.isOpen) {
          this.close();
        } else {
          this.open();
        }
      }

      open() {
        this.panel.hidden = false;
        this.trigger.setAttribute('aria-expanded', 'true');
        this.isOpen = true;
      }

      close() {
        this.panel.hidden = true;
        this.trigger.setAttribute('aria-expanded', 'false');
        this.isOpen = false;
      }

      handleOutsideClick(event) {
        if (this.isOpen && !this.contains(event.target)) this.close();
      }

      handleKeydown(event) {
        if (event.key === 'Escape' && this.isOpen) {
          this.close();
          this.trigger.focus();
        }
      }

      handleChange(event) {
        const input = event.target;
        if (input.type !== 'radio') return;
        this.pick(input);
      }

      // After the first pick, the server marks every option's current value
      // checked. Clicking that already-checked row fires no change event, so
      // count the click itself as the pick (no variant change is needed).
      handleRowClick(event) {
        const label = event.target.closest('.listbox-picker__option-label');
        if (!label) return;
        const input = this.querySelector(`#${CSS.escape(label.htmlFor)}`);
        if (!input?.checked) return;
        this.pick(input);
        // No variant change follows, so show the finish's photo now if a
        // Size or Option pick already fetched it (product-info.js).
        if (this.isFinish) this.container?.showPendingVariantMedia?.();
      }

      get isFinish() {
        return this.dataset.optionName === 'Finish';
      }

      pick(input) {
        sessionStorage.setItem(this.pickedKey, '1');
        // The gallery follows the finish only once one is picked on this
        // page view (product-info.js, updateMedia). Set before the variant
        // fetch resolves: the change event reaches this picker first.
        if (this.isFinish) this.container?.setAttribute('data-finish-picked', '');
        this.applySelection(input);
        this.close();
        this.trigger.focus();
        updatePurchaseGate(this.container);
      }

      applySelection(input) {
        this.label.textContent = `${this.dataset.optionName}: ${input.dataset.valueName}`;
        this.swatch.innerHTML = '';
        const src = input.dataset.swatchSrc;
        this.swatch.hidden = !src;
        if (!src) return;
        const img = document.createElement('img');
        img.src = src;
        img.width = 40;
        img.height = 40;
        img.alt = '';
        this.swatch.appendChild(img);
      }
    },
  );
}
